import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAuthorization } from '@/contexts/AuthorizationContext';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { FileSignature, Loader2, ExternalLink, Mail } from 'lucide-react';

const POLL_INTERVAL_MS = 5000;

export default function AssinaturaContratoPlataforma() {
  const { user } = useAuth();
  const { userProfile, userRole, refreshUserProfile } = useAuthorization();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [contratoEnviado, setContratoEnviado] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const verificarAssinatura = async (): Promise<boolean> => {
    try {
      const res = await fetchWithAuth('/api/assinafy/verificar-contrato-plataforma');
      const data = await res.json();
      if (data.assinado) {
        await refreshUserProfile();
        toast({ title: 'Contrato assinado com sucesso!', description: 'Redirecionando...' });
        navigate('/dashboard', { replace: true });
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  // Verificação automática em polling quando contrato foi enviado
  useEffect(() => {
    if (!contratoEnviado || !user) return;
    const poll = async () => {
      const assinado = await verificarAssinatura();
      if (assinado && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [contratoEnviado, user]);

  // Redirecionar se não precisa assinar; detectar se contrato já foi enviado
  useEffect(() => {
    if (!user) {
      navigate('/auth', { replace: true });
      return;
    }
    if (!userProfile) return;
    if (userRole !== 'parceiro' && userRole !== 'vendedor') {
      navigate('/dashboard', { replace: true });
      return;
    }
    const assinado = !!(userProfile as { contrato_plataforma_assinado_em?: string | null })?.contrato_plataforma_assinado_em;
    if (assinado) {
      navigate('/dashboard', { replace: true });
      return;
    }
    const checkPending = async () => {
      try {
        const res = await fetchWithAuth('/api/assinafy/verificar-contrato-plataforma');
        const data = await res.json();
        if (data.assinado) {
          await refreshUserProfile();
          navigate('/dashboard', { replace: true });
          return;
        }
        if (data.pendente) {
          setContratoEnviado(true);
        }
      } finally {
        setChecking(false);
      }
    };
    checkPending();
  }, [user, userProfile, userRole, navigate]);

  const handleAssinar = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth('/api/assinafy/enviar-contrato-plataforma', {
        method: 'POST',
      });
      const data = await res.json();

      if (!res.ok) {
        if (res.status === 400 && data.error?.includes('já assinou')) {
          await refreshUserProfile();
          toast({ title: 'Contrato já assinado', description: 'Redirecionando...' });
          navigate('/dashboard', { replace: true });
          return;
        }
        const errMsg = data.instrucoes
          ? `${data.error} ${data.instrucoes}`
          : data.error || 'Erro ao enviar contrato';
        throw new Error(errMsg);
      }

      if (data.jaAssinado) {
        await refreshUserProfile();
        toast({ title: 'Contrato já assinado', description: 'Redirecionando...' });
        navigate('/dashboard', { replace: true });
        return;
      }

      setContratoEnviado(true);
      toast({
        title: 'Contrato enviado por e-mail',
        description: 'Verifique seu e-mail e realize a assinatura do contrato. Assim que identificarmos a assinatura, você será redirecionado automaticamente.',
        duration: 8000,
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao enviar contrato';
      const descricao = msg.includes('ASSINAFY_ACCOUNT_ID')
        ? 'O Assinafy não está configurado. Configure ASSINAFY_ACCOUNT_ID no backend (app.assinafy.com.br > Minha Conta > Workspaces). Entre em contato com o administrador.'
        : msg.includes('ASSINAFY_API_KEY')
          ? 'Configure a chave ASSINAFY_API_KEY no backend. Entre em contato com o administrador.'
          : msg;
      toast({ title: 'Erro', description: descricao, variant: 'destructive', duration: 10000 });
    } finally {
      setLoading(false);
    }
  };

  if (checking || !user || !userProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
            <FileSignature className="w-10 h-10 text-amber-600 dark:text-amber-500" />
          </div>
          <CardTitle className="text-2xl">Assinatura do Contrato de Plataforma</CardTitle>
          <CardDescription className="text-base">
            Olá, {userProfile.nome}! Para acessar o Sun7 Link, você precisa assinar o contrato de adesão à plataforma.
            Seu acesso será permitido mediante a assinatura do contrato.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2">
            <Button
              onClick={handleAssinar}
              disabled={loading || contratoEnviado}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Preparando...
                </>
              ) : contratoEnviado ? (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Contrato enviado por e-mail
                </>
              ) : (
                <>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Assinar Contrato via Assinafy
                </>
              )}
            </Button>
          </div>

          {contratoEnviado && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40 p-4 flex items-center gap-3">
              <Loader2 className="h-5 w-5 shrink-0 animate-spin text-amber-600 dark:text-amber-500" />
              <p className="text-sm text-amber-800 dark:text-amber-200">
                A assinatura do contrato ainda não foi identificada. Verifique seu e-mail, assine o documento e aguarde — você será redirecionado automaticamente para o painel assim que a assinatura for confirmada.
              </p>
            </div>
          )}

          {!contratoEnviado && (
            <p className="text-xs text-center text-muted-foreground">
              Ao clicar em &quot;Assinar Contrato via Assinafy&quot;, o contrato será enviado para o seu e-mail. Acesse o link recebido para realizar a assinatura.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
